import { Minus, Plus } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from 'react-aria-components';
import { useDebounce } from 'use-debounce';
import { useToasts } from '~/components/Toast';
import { useJourneyActions } from '~/stores/journeyStore';
import type { StayTransformed } from '~/types/JourneyItem.types';
import { logError } from '~/utils';
import { syncUnavailableStartDates } from '~/utils/unavailableDates';
import styles from './NightsAdjuster.module.css';
interface OverviewNightsAdjusterProps {
  item: StayTransformed;
  journeyId: string;
}
export function OverviewNightsAdjuster({
  item,
  journeyId
}: Readonly<OverviewNightsAdjusterProps>) {
  const t = useTranslations();
  const [localNights, setLocalNights] = useState(item.nights);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedNights, setDebouncedNights] = useDebounce(localNights, 2000);
  const {
    setJourney
  } = useJourneyActions();
  const toasts = useToasts();
  const isMinReached = localNights <= 1;
  const isMaxReached = localNights >= 30;
  const handleChange = (nightsChange: number) => {
    if (isMaxReached && nightsChange > 0 || isMinReached && nightsChange < 0) return;
    setLocalNights(localNights + nightsChange);
  };

  // Re-sync local state when the journey store updates this stay's nights from elsewhere.
  // Skipped mid-submit so we don't clobber an in-flight user change.
  useEffect(() => {
    if (!isLoading && item.nights !== localNights) {
      setLocalNights(item.nights);
      setDebouncedNights(item.nights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.nights]);

  // Effect intentionally depends only on debouncedNights: the latest item/journeyId/setJourney/toasts
  // are captured by the closure when the debounce fires, which is the values we want to send.
  useEffect(() => {
    const changeDuration = async () => {
      if (debouncedNights === item.nights) return;

      // Snapshot pre-change nights so revert paths can't pick up a store update
      // that lands between fetch resolution and the finally block.
      const previousNights = item.nights;
      setIsLoading(true);
      try {
        const response = await fetch('/api/journey/change-duration', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            journeyId,
            stayItemId: item.stayItemId,
            newDurationNights: debouncedNights
          })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const {
          journey,
          success,
          unavailableStartDatesForPinnedAccommodation
        } = await response.json();
        if (journey && success) {
          setJourney(journey);
          syncUnavailableStartDates(unavailableStartDatesForPinnedAccommodation);
          toasts.add({
            title: t('content.feedback.nights_update_success', {
              place: item.title
            }),
            variant: 'success',
            position: 'middle'
          }, {
            timeout: 5000
          });
        } else {
          setLocalNights(previousNights);
          setDebouncedNights(previousNights);
          toasts.add({
            title: t('content.feedback.error_title'),
            description: t('content.feedback.nights_update_unavailable', {
              nights: debouncedNights
            }),
            variant: 'warning',
            position: 'middle'
          }, {
            timeout: 5000
          });
        }
      } catch (error) {
        logError(`Error updating nights for stayItemId:${item.stayItemId} on journeyId:${journeyId} - ${error}`);
        setLocalNights(previousNights);
        setDebouncedNights(previousNights);
        toasts.add({
          title: t('content.feedback.error_title'),
          description: t('content.feedback.error_generic'),
          variant: 'warning',
          position: 'middle'
        }, {
          timeout: 5000
        });
      } finally {
        setIsLoading(false);
      }
    };
    changeDuration();
  }, [debouncedNights]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className={styles.controlsAndLoader}>
      <div className={styles.controls}>
        <Button className={clsx(styles.button, {
        [styles.disabled]: isMinReached || isLoading
      })} isDisabled={isMinReached || isLoading} onPress={() => handleChange(-1)} aria-label={t('aria.decrease_nights_at', {
        place: item.title
      })}>
          <Minus size="small" decorative />
        </Button>
        <span className={styles.nightsLabel}>{localNights}</span>
        <Button className={clsx(styles.button, {
        [styles.disabled]: isMaxReached || isLoading
      })} isDisabled={isMaxReached || isLoading} onPress={() => handleChange(1)} aria-label={t('aria.increase_nights_at', {
        place: item.title
      })}>
          <Plus size="small" decorative />
        </Button>
      </div>
      {isLoading && <div className={styles.loadingIndicator}>
          <div className={styles.loadingDot} />
          <div className={styles.loadingDot} />
          <div className={styles.loadingDot} />
        </div>}
    </div>;
}